import { Check, Copy, Rocket, ScanSearch, TrendingDown } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Badge, Button, cn, Dialog, ErrorState, formatDuration, formatInt, plural, Pressable, RelativeTime, Select, Skeleton, toast, toUiError, TONE_CLASS } from '@orgus/ui';
import { disciplineRuleInfo } from '@orgus/protocol';
import type { DisciplineState, Project } from '@orgus/protocol';
import { useDisciplineActions } from './actions.ts';
import { healthOfScore, HEALTH_TONE } from './model.ts';

function ScoreBar({ score }: { score: number }) {
  const tone = HEALTH_TONE[healthOfScore(score)];
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-24 overflow-hidden rounded-full bg-border-subtle" role="img" aria-label={`Score ${score} of 100`}>
        <span className={cn('block h-full rounded-full', TONE_CLASS[tone].bg)} style={{ width: `${score}%` }} />
      </span>
      <span className={cn('num w-7 text-right text-sm font-semibold', TONE_CLASS[tone].fg)}>{score}</span>
    </span>
  );
}

function Choice({ on, onClick, title, children }: { on: boolean; onClick: () => void; title: string; children: ReactNode }) {
  return (
    <Pressable onClick={onClick} aria-pressed={on} className={cn('flex flex-1 flex-col gap-1 rounded-lg border p-3 text-left', on ? 'border-accent bg-accent-soft' : 'border-border hover:bg-hover')}>
      <span className="flex items-center gap-2 text-sm font-medium text-fg">
        {on ? <Check className="size-3.5 text-accent" aria-hidden /> : <span className="size-3.5" aria-hidden />}
        {title}
      </span>
      <span className="text-sm text-fg-muted">{children}</span>
    </Pressable>
  );
}

/**
 * Onboard a repo: scan it, see how every pack scores and what is suggested, then start the
 * ratchet so new code must comply while the existing debt is tracked and burned down.
 */
export function OnboardDialog({ projects, state, initial, onClose }: { projects: Project[]; state: DisciplineState | undefined; initial: string | null; onClose: () => void }) {
  const actions = useDisciplineActions();
  const [projectId, setProjectId] = useState(initial ?? projects[0]?.id ?? '');
  const [use, setUse] = useState<'suggested' | 'current'>('suggested');
  const [busy, setBusy] = useState<'scan' | 'adopt' | null>(null);
  // What failed, said at the top of the dialog: errors never go in a toast.
  const [error, setError] = useState<unknown>(null);
  const scan = state?.scans?.find((s) => s.projectId === projectId);
  const scanning = !!state?.pending?.scans.includes(projectId);
  const baseline = state?.baselines?.[projectId];
  const name = projects.find((p) => p.id === projectId)?.name ?? projectId;

  const run = async () => {
    setBusy('scan');
    setError(null);
    try {
      await actions.onboard(projectId);
    } catch (e) {
      setError(toUiError(e, { what: 'The scan did not start.', next: 'Make sure the machine holding the repo is online.' }));
    } finally {
      setBusy(null);
    }
  };
  const adopt = async () => {
    setBusy('adopt');
    setError(null);
    try {
      await actions.adopt(projectId, use);
      toast.success(`Ratchet started for ${name}`, use === 'suggested' ? 'The suggested rules are kept in Orgus; today\'s debt is tracked and new code must comply.' : 'Today\'s debt under the current rules is tracked; new code must comply.');
      onClose();
    } catch (e) {
      setError(toUiError(e, { what: 'The ratchet did not start.', next: 'Only an owner or admin can start it.' }));
    } finally {
      setBusy(null);
    }
  };
  const copy = async () => {
    if (!scan) return;
    setError(null);
    try {
      await navigator.clipboard.writeText(scan.suggested.yaml);
      toast.success('Copied', 'Paste it into .orgus/discipline.yaml in the repo and commit it.');
    } catch {
      setError({ what: 'The rulebook was not copied.', why: 'The browser did not allow it.', next: 'Select the text and copy it by hand.' });
    }
  };

  const ready = scan && !scan.error && !scanning;
  return (
    <Dialog
      open
      size="xl"
      onOpenChange={(o) => !o && busy !== 'adopt' && onClose()}
      title="Onboard a repo"
      description="Score each rule pack and hold new code to it; nothing is written into the repo."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy === 'adopt'}>
            Close
          </Button>
          {ready ? (
            <Button variant="primary" icon={<TrendingDown />} onClick={() => void adopt()} loading={busy === 'adopt'}>
              {baseline ? 'Restart the ratchet' : 'Start the ratchet'}
            </Button>
          ) : (
            <Button variant="primary" icon={<ScanSearch />} onClick={() => void run()} loading={busy === 'scan' || scanning} disabled={!projectId}>
              {scanning ? 'Scanning' : 'Scan repo'}
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error != null && <ErrorState variant="inline" error={error} />}
        <div className="flex flex-wrap items-end gap-3">
          <Select label="Repo" value={projectId} onValueChange={setProjectId} options={projects.map((p) => ({ value: p.id, label: p.name }))} wrapperClassName="min-w-48" />
          {scan && !scanning && (
            <span className="text-sm text-fg-subtle">
              Scanned <RelativeTime ts={scan.at} /> in {formatDuration(scan.durationMs)}.{' '}
              <Button size="sm" variant="ghost" icon={<ScanSearch />} onClick={() => void run()} loading={busy === 'scan'}>
                Scan again
              </Button>
            </span>
          )}
        </div>

        {scanning ? (
          <div className="flex flex-col gap-3" role="status" aria-label={`Scanning ${name}`}>
            <p className="text-sm text-fg-muted">Scoring every pack against {name}, up to a minute for large repos.</p>
            <Skeleton className="h-16 rounded-lg" />
            <Skeleton className="h-40 rounded-lg" />
          </div>
        ) : !scan ? (
          <p className="text-sm text-fg-muted">Scan {name} to get a baseline report and a suggested rulebook. The machine holding it reads the files; nothing is written.</p>
        ) : scan.error ? (
          <ErrorState variant="inline" error={{ what: `Could not scan ${name}.`, why: scan.error, next: 'Make sure the checkout exists on that machine and its agent is online, then scan again.' }} onRetry={run} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm text-fg-muted">
              <span>
                {formatInt(scan.files)} files, {plural(scan.units, 'unit')}
              </span>
              {scan.languages.map((l) => (
                <Badge key={l.name} size="sm" variant="outline">
                  {l.name} {formatInt(l.files)}
                </Badge>
              ))}
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
              <section className="flex flex-col gap-2" aria-label="How each pack scores">
                <h3 className="text-sm font-medium text-fg-muted">How each pack scores today</h3>
                <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
                  {scan.packs.map((p) => (
                    <li key={p.pack} className="flex flex-col gap-1 px-3 py-2">
                      <span className="flex items-center justify-between gap-3">
                        <span className="flex items-center gap-2 font-mono text-sm text-fg">
                          {p.pack}
                          {scan.suggested.extends.includes(p.pack) && (
                            <Badge size="sm" tone="accent" icon={<Check />}>
                              Suggested
                            </Badge>
                          )}
                        </span>
                        <ScoreBar score={p.score} />
                      </span>
                      {p.failing.length > 0 && <span className="truncate text-xs text-fg-subtle">{p.failing.slice(0, 3).map((f) => `${disciplineRuleInfo(f.rule).title} (${plural(f.files, 'file')})`).join(', ')}</span>}
                    </li>
                  ))}
                </ul>
              </section>
              <section className="flex flex-col gap-2" aria-label="Suggested rulebook">
                <h3 className="text-sm font-medium text-fg-muted">Suggested rulebook</h3>
                <ul className="flex flex-col gap-1">
                  {scan.suggested.why.map((w) => (
                    <li key={w} className="flex items-start gap-2 text-sm text-fg-muted">
                      <Check className="mt-0.5 size-3.5 shrink-0 text-passed" aria-hidden />
                      {w}
                    </li>
                  ))}
                </ul>
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md border border-border-subtle bg-bg p-3 font-mono text-xs text-fg">{scan.suggested.yaml}</pre>
                <span>
                  <Button size="sm" variant="ghost" icon={<Copy />} onClick={() => void copy()}>
                    Copy YAML
                  </Button>
                </span>
              </section>
            </div>
            <section className="flex flex-col gap-2" aria-label="Start the ratchet">
              <h3 className="text-sm font-medium text-fg-muted">Start the ratchet{baseline ? ' (replaces the current one)' : ''}</h3>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Choice on={use === 'suggested'} onClick={() => setUse('suggested')} title="Use the suggested rules">
                  Score {scan.suggested.score} today; {plural(scan.baseline.total, 'existing violation')} become tracked debt. The rules are kept in Orgus until you commit the YAML.
                </Choice>
                <Choice on={use === 'current'} onClick={() => setUse('current')} title="Keep the current rules">
                  {scan.current.rulebook === 'repo' ? "The repo's own rulebook" : 'The recommended pack'}: score {scan.current.score}, {plural(scan.current.total, 'existing violation')} become tracked debt.
                </Choice>
              </div>
              <p className="flex items-center gap-1.5 text-xs text-fg-subtle">
                <Rocket className="size-3" aria-hidden />
                From then on only new violations count against sessions; fixed debt leaves the baseline for good.
              </p>
            </section>
          </>
        )}
      </div>
    </Dialog>
  );
}
